import {
  ALLOWED_AVATAR_TYPES,
  AVATAR_MAX_BYTES,
  avatarUploadFileName,
  normalizeImageMime,
  type AvatarPickerFile,
} from './prepare-avatar-core';

export {
  ALLOWED_AVATAR_TYPES,
  AVATAR_MAX_BYTES,
  avatarUploadFileName,
  normalizeImageMime,
  readApiErrorMessage,
} from './prepare-avatar-core';
export type { AvatarPickerFile } from './prepare-avatar-core';

async function rasterizeToJpeg(blob: Blob) {
  const bitmap = await createImageBitmap(blob);
  const maxEdge = 1024;
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height, 1));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not prepare photo');
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const jpeg = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(resolve, 'image/jpeg', 0.85);
  });
  bitmap.close();
  if (!jpeg) {
    throw new Error('Could not prepare photo');
  }
  return jpeg;
}

export async function prepareAvatarUpload(input: AvatarPickerFile) {
  const source = input.file
    ?? (input.uri.startsWith('blob:') || input.uri.startsWith('data:')
      ? await fetch(input.uri).then(response => response.blob())
      : null);

  if (!source) {
    return {
      uri: input.uri,
      type: normalizeImageMime(input.type) || 'image/jpeg',
      name: avatarUploadFileName(input.name, normalizeImageMime(input.type) || 'image/jpeg'),
    };
  }

  if (source.size > AVATAR_MAX_BYTES) {
    throw new Error('PHOTO_TOO_LARGE');
  }

  const sourceType = normalizeImageMime(input.type || source.type);
  const needsConvert = !sourceType || !ALLOWED_AVATAR_TYPES.has(sourceType) || !source.type;
  const prepared = needsConvert ? await rasterizeToJpeg(source) : source;
  const type = needsConvert ? 'image/jpeg' : sourceType;
  const name = avatarUploadFileName(input.name, type);
  return {
    uri: input.uri,
    type,
    name,
    file: prepared,
  };
}
